import React, { useState, useMemo } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  calculateFingerDurationDelta,
  calculateHandDurationDelta,
  calculateAverage,
  calculateTrend,
  getExercisesByType,
  calculateHeatmapColor
} from '../../utils/articulationUtils';

const ArticulationHeatmap = ({ sessions, timeMode, onCellClick }) => {
  const [expandedTypes, setExpandedTypes] = useState(new Set());

  // Helper to format exercise names
  const formatExerciseName = (name) => {
    if (!name) return '';
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Column definitions
  const columns = [
    { id: 'lh', label: 'LH', entity: 'left', isHand: true },
    { id: 'l1', label: 'L1', entity: 'left', finger: 1 },
    { id: 'l2', label: 'L2', entity: 'left', finger: 2 },
    { id: 'l3', label: 'L3', entity: 'left', finger: 3 },
    { id: 'l4', label: 'L4', entity: 'left', finger: 4 },
    { id: 'l5', label: 'L5', entity: 'left', finger: 5 },
    { id: 'rh', label: 'RH', entity: 'right', isHand: true },
    { id: 'r1', label: 'R1', entity: 'right', finger: 1 },
    { id: 'r2', label: 'R2', entity: 'right', finger: 2 },
    { id: 'r3', label: 'R3', entity: 'right', finger: 3 },
    { id: 'r4', label: 'R4', entity: 'right', finger: 4 },
    { id: 'r5', label: 'R5', entity: 'right', finger: 5 },
  ];

  // Get exercise types and exercises
  const exerciseTypes = useMemo(() => {
    const types = ['scale', 'arpeggio'];
    return types;
  }, []);

  const exercisesByType = useMemo(() => {
    const result = {};
    exerciseTypes.forEach(type => {
      result[type] = getExercisesByType(sessions, type);
    });
    return result;
  }, [sessions, exerciseTypes]);

  // Toggle expansion
  const toggleExpand = (type) => {
    const newExpanded = new Set(expandedTypes);
    if (newExpanded.has(type)) {
      newExpanded.delete(type);
    } else {
      newExpanded.add(type);
    }
    setExpandedTypes(newExpanded);
  };

  // Calculate cell value
  const calculateCellValue = (column, exerciseOrType) => {
    // Filter sessions for this exercise or type
    const filteredSessions = sessions.filter(s => {
      return s.exercise_name === exerciseOrType || s.exercise_type === exerciseOrType;
    });

    if (filteredSessions.length === 0) return null;

    // Build field name
    let field;
    if (column.isHand) {
      field = `${column.entity}_relative_duration_avg`;
    } else {
      field = `${column.entity}_finger_${column.finger}_relative_duration_avg`;
    }

    // Calculate value based on time mode
    if (timeMode === 'trend') {
      const trend = calculateTrend(filteredSessions, field);
      return trend.difference;
    } else {
      return calculateAverage(filteredSessions, field);
    }
  };

  // Render a cell
  const renderCell = (column, exerciseOrType, isExercise = false) => {
    const value = calculateCellValue(column, exerciseOrType);
    const backgroundColor = calculateHeatmapColor(value);
    
    // Build entity string for callback
    const entityStr = column.isHand 
      ? `${column.entity}_hand`
      : `${column.entity}_finger_${column.finger}`;
    
    return (
      <td
        key={`${column.id}-${exerciseOrType}`}
        className="px-1 py-1 text-center cursor-pointer hover:ring-2 hover:ring-gray-400 transition-all"
        style={{
          backgroundColor,
          width: '32px',
          height: '24px'
        }}
        onClick={() => onCellClick({ 
          entity: entityStr, 
          exercise: isExercise ? exerciseOrType : null,
          exerciseType: !isExercise ? exerciseOrType : sessions.find(s => s.exercise_name === exerciseOrType)?.exercise_type
        })}
        title={value !== null ? `${value.toFixed(1)}%` : 'No data'}
      >
      </td>
    );
  };

  return (
    <div className="flex flex-col bg-white rounded-lg shadow overflow-hidden">
      <div className="p-3 border-b border-gray-200">
        <h2 className="text-lg font-bold text-gray-900 mb-2">Note Duration by Exercise</h2>
        
        {/* Color Legend - Bidirectional (Purple to White to Orange) */}
        <div className="flex items-center gap-3 text-xs">
          <span className="text-gray-600 font-semibold">Legend:</span>
          <div className="flex items-center">
            <span className="text-[10px] text-gray-600 mr-2">Too Short</span>
            <div className="flex items-start">
              {[-20, -15, -10, -5, 0, 5, 10, 15, 20].map((val, idx) => (
                <div key={idx} className="flex flex-col items-center">
                  <div 
                    className="w-6 h-3 border-r border-gray-300 first:border-l"
                    style={{ 
                      backgroundColor: calculateHeatmapColor(val)
                    }}
                  ></div>
                  <span className={`text-[10px] mt-0.5 ${(val === -20 || val === 0 || val === 20) ? 'text-gray-500' : 'text-transparent'}`}>
                    {val === -20 || val === 0 || val === 20 ? `${val}%` : '0'}
                  </span>
                </div>
              ))}
            </div>
            <span className="text-[10px] text-gray-600 ml-2">Too Long</span>
          </div>
          <span className="text-gray-500 text-[10px]">(Duration Δ)</span>
        </div>
      </div>
      
      <div className="overflow-auto max-h-96">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 bg-gray-100 z-10">
            <tr>
              <th className="px-2 py-1 text-left text-[11px] font-semibold text-gray-700 border-b border-gray-300" style={{ width: '120px', minWidth: '120px', maxWidth: '120px' }}>
                Exercise
              </th>
              {columns.map(col => (
                <th 
                  key={col.id}
                  className="px-1 py-1 text-center text-[10px] font-semibold text-gray-700 border-b border-gray-300"
                  style={{ width: '32px' }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {exerciseTypes.map(type => (
              <React.Fragment key={type}>
                {/* Exercise Type Row */}
                <tr className="bg-gray-50 hover:bg-gray-100 border-b border-gray-200">
                  <td className="px-2 py-1.5 text-[11px] font-semibold text-gray-900" style={{ width: '120px', minWidth: '120px', maxWidth: '120px' }}>
                    <button
                      onClick={() => toggleExpand(type)}
                      className="flex items-center space-x-1 w-full text-left"
                    >
                      {expandedTypes.has(type) ? (
                        <ChevronDownIcon className="h-3 w-3 flex-shrink-0" />
                      ) : (
                        <ChevronRightIcon className="h-3 w-3 flex-shrink-0" />
                      )}
                      <span className="capitalize truncate">{type}s</span>
                    </button>
                  </td>
                  {columns.map(col => renderCell(col, type, false))}
                </tr>

                {/* Expanded Exercise Rows */}
                {expandedTypes.has(type) && exercisesByType[type].map(exercise => (
                  <tr key={exercise} className="hover:bg-gray-50 border-b border-gray-100">
                    <td className="px-2 pl-6 py-1 text-[10px] text-gray-700 truncate" style={{ width: '120px', minWidth: '120px', maxWidth: '120px' }} title={formatExerciseName(exercise)}>
                      {formatExerciseName(exercise)}
                    </td>
                    {columns.map(col => renderCell(col, exercise, true))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ArticulationHeatmap;

